import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { prefersReducedMotion } from "@/utils/motion";

// Update when the device contact channel is ready
export const DEVICE_CONTACT_URL = "#";

const REC_START_SECONDS = 5327;
const DEVICE_LATENCY_BASE = 0.27;
const DEVICE_LATENCY_JITTER = 0.06;
// A clear traffic-ahead frame inside the 27.6 s shared loop (see
// docs/development/hero-video.md) — used as the reduced-motion still
const REDUCED_MOTION_FRAME_SECONDS = 6;

function formatTimecode(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
}

export default function TrackcastHero() {
  const { t } = useTranslation("pages");
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduced = prefersReducedMotion();
  const [recSeconds, setRecSeconds] = useState(REC_START_SECONDS);
  const [latency, setLatency] = useState<string>(
    reduced ? "+0.2–0.3 s" : "+0.27 s",
  );

  useEffect(() => {
    if (reduced) {
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.currentTime = REDUCED_MOTION_FRAME_SECONDS;
      }
      return;
    }
    const recTimer = setInterval(() => {
      setRecSeconds((s) => s + 1);
    }, 1000);
    const latencyTimer = setInterval(() => {
      const value =
        DEVICE_LATENCY_BASE + (Math.random() - 0.5) * DEVICE_LATENCY_JITTER;
      setLatency(`+${value.toFixed(2)} s`);
    }, 900);
    return () => {
      clearInterval(recTimer);
      clearInterval(latencyTimer);
    };
  }, [reduced]);

  return (
    <section className="bg-gray-950 text-white rounded-2xl p-7 sm:p-10 grid gap-10 lg:grid-cols-2 lg:items-center">
      <div>
        <p className="text-[11px] font-semibold tracking-[0.2em] text-gray-400 uppercase mb-4">
          {t("trackcast.hero.eyebrow")}
        </p>
        <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-tight mb-4">
          {t("trackcast.hero.headline1")}
          <br />
          <span className="text-gray-400">{t("trackcast.hero.headline2")}</span>
        </h2>
        <p className="text-gray-300 mb-7 max-w-md">{t("trackcast.hero.lead")}</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/trackcast/android" className="btn-primary px-5">
            {t("trackcast.hero.ctaApp")}
          </Link>
          <a
            href="#trackcast-device"
            className="btn bg-white/10 text-white hover:bg-white/20 px-5"
          >
            {t("trackcast.hero.ctaDevice")}
          </a>
        </div>
      </div>

      <div
        className="relative aspect-video rounded-xl border border-gray-800 overflow-hidden bg-black"
        role="img"
        aria-label={t("trackcast.hero.videoAlt")}
      >
        {/* Broadcast chrome labels are stream UI and stay untranslated, like real timing screens */}
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src="/videos/home-hero-loop.mp4"
          poster="/videos/home-hero-loop-poster.jpg"
          autoPlay={!reduced}
          muted
          loop
          playsInline
          aria-hidden="true"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2 rounded-md border border-gray-800 bg-gray-950/60 px-2.5 py-1.5 font-mono text-xs text-gray-200">
          <span className="h-2 w-2 rounded-full bg-red-500 motion-safe:animate-pulse" />
          REC {formatTimecode(recSeconds)}
        </div>
        <div className="absolute bottom-3 left-3 rounded-md border border-gray-800 bg-gray-950/60 px-2.5 py-1.5 font-mono text-xs text-gray-400 max-sm:hidden">
          SUPERRACE R4 · ONBOARD
        </div>
        <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-md border border-gray-800 bg-gray-950/60 px-2.5 py-1.5 font-mono text-xs">
          <span className="rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold tracking-[0.1em] text-white">
            LIVE
          </span>
          <span className="tabular-nums font-semibold text-primary-400">
            {latency}
          </span>
        </div>
      </div>
    </section>
  );
}
